import { query as clientQuery } from '@kurtosys/ksys-api-client';
import { shared } from '@kurtosys/ksys-app-components';
import { common, query } from '@kurtosys/ksys-app-template';
import { deepMergeObjectsWithOptions } from '@kurtosys/ksys-app-template/dist/common/commonUtils/object.js';
import { IBatchOptions, IBatchPromiseElement } from '@kurtosys/types/api/common/index.js';
import { IFundManager } from '@kurtosys/types/api/dictionary/index.js';
import { IStatistics } from '@kurtosys/types/api/search/index.js';
import {
	IConfigurationComponentOrchestration,
	IEntitiesRequestOptions,
	IPagingResult,
} from '@kurtosys/types/app/index.js';
import { IManifest } from '@kurtosys/types/appsManager/IManifest.js';
import { IMergeOptions } from '@kurtosys/types/common/IMergeOptions.js';
import { IPagingOptions } from '@kurtosys/types/common/IPagingOptions.js';
import { IContextAndConfiguration } from '@kurtosys/types/context/IContextAndConfiguration.js';
import { IGetContextOptions } from '@kurtosys/types/context/IGetContextOptions.js';
import { ISetContextOptions } from '@kurtosys/types/context/ISetContextOptions.js';
import {
	IExecutionOptions,
	IQueryContext,
	IQueryContextConfiguration,
	IQueryContextCulture,
	IQueryContextEntity,
	IQueryOptions,
} from '@kurtosys/types/query/index.js';
import { action, computed, observable } from 'mobx';

import { IComponentStylesBase } from '../models/IComponentStylesBase.js';
import { IConfigurationBase } from '../models/IConfigurationBase.js';

import { AppStoreBase } from './base/AppStoreBase.js';
import { InputStore } from './InputStore.js';
import { KurtosysApiStore } from './KurtosysApiStore.js';
import { StoreContext } from './StoreContext.js';
import { TranslationStore } from './TranslationStore.js';

type IQueryContextClient = clientQuery.IQueryContextClient;
const QueryContext = clientQuery.QueryContext;

export class QueryStore<
	TConfiguration extends IConfigurationBase,
	TComponentStyles extends IComponentStylesBase,
> extends shared.QueryStore {
	manifest: IManifest;
	storeContext: StoreContext<TConfiguration, TComponentStyles, any, any, any>;
	@observable.ref context: IQueryContext | undefined = undefined;
	@observable.ref fundManagerContext: IFundManager | undefined = undefined;
	@observable.ref responseContext: object | undefined = undefined;
	@observable.ref statisticContext: IStatistics | undefined = undefined;
	@observable.ref private updatedInputs: Record<string, any> = {};

	constructor(storeContext: StoreContext<TConfiguration, TComponentStyles, any, any, any>, manifest: IManifest) {
		super();
		this.storeContext = storeContext;
		this.manifest = manifest;
	}

	@computed
	get appStore() {
		return this.storeContext.get<AppStoreBase<TConfiguration, TComponentStyles>>('appStore');
	}

	@computed
	get inputStore() {
		return this.storeContext.get<InputStore<TConfiguration, TComponentStyles>>('inputStore');
	}

	@computed
	get translationStore() {
		return this.storeContext.get<TranslationStore<TConfiguration, TComponentStyles>>('translationStore');
	}

	@computed
	get kurtosysApiStore() {
		return this.storeContext.get<KurtosysApiStore<TConfiguration, TComponentStyles>>('kurtosysApiStore');
	}

	@computed
	get client(): IQueryContextClient {
		return this.kurtosysApiStore.client;
	}

	@computed
	get inputs(): Record<string, any> {
		const { appParamsHelper } = this.appStore;
		return { ...appParamsHelper.inputs, ...this.updatedInputs };
	}

	@computed
	get executionOptions(): IExecutionOptions {
		return {
			applicationGuid: this.appStore.applicationGuid,
			defaultContextParentKey: this.defaultContextParentKey,
			context: this.context,
			responseContext: this.responseContext,
			fundManagerContext: this.fundManagerContext,
			statisticContext: this.statisticContext,
			culture: this.translationStore.culture,
			fallbackCulture:
				this.appStore.configuration &&
				this.appStore.configuration.culture &&
				this.appStore.configuration.culture.default,
			translate: this.translationStore.translate,
			inputs: this.inputs,
			serviceUrl: this.appStore.serviceUrl,
			globalFormatsByKey: this.appStore.globalFormatsByKey,
		};
	}
	get defaultContextParentKey(): string {
		const { configuration, storeKey } = this.appStore;
		let response: string = storeKey;
		if (configuration) {
			const { data } = configuration;
			if (data) {
				const { key } = data;
				if (key) {
					response = key;
				}
			}
		}
		return response;
	}

	/**
	 * Returns the target context & contextConfiguration
	 *
	 * Note: contextKey is sourced from the IQueryOptions object, defaults to `default`
	 *
	 * @param [targetContext={ contextParentKey: this.defaultContextParentKey, contextKey: 'default' }]
	 */
	getContext(context?: IGetContextOptions): IContextAndConfiguration {
		const targetContext = { 
			contextParentKey: context?.contextParentKey || this.defaultContextParentKey,
			contextKey: context?.contextKey || 'default'
		};
		return query.queryUtils.queryHelper.contextStore().get(targetContext) ?? {};
	}

	/**
	 * Set the target context & contextConfiguration with a new value
	 *
	 * Note: contextKey is sourced from the IQueryOptions object, defaults to `default`
	 *
	 * @param targetContext
	 */
	setContext(targetContext: ISetContextOptions) {
		const { contextParentKey = this.defaultContextParentKey, contextKey = 'default', value } = targetContext;
		query.queryUtils.queryHelper.contextStore().set({
			contextParentKey,
			contextKey,
			value,
		});
	}

	@computed
	get dataContextConfigurations() {
		const rawContexts = this.appStore.configuration?.data?.context;
		if (!rawContexts) {
			return [];
		}
		const contexts = !Array.isArray(rawContexts) ? [rawContexts] : rawContexts;
		return contexts.map((context, contextIndex) => {
			const defaultContextKey = `default${contextIndex > 0 ? contextIndex + 1 : ''}`;
			if (!context.contextKey) {
				context.contextKey = defaultContextKey;
			}
			return context;
		});
	}

	@action
	updateContextConfiguration(
		contextKey: string,
		dataContextOverride: Partial<IQueryContextConfiguration>,
		deepMergeObjectOptions: IMergeOptions = {},
	) {
		const targetContextKey = contextKey || 'default';
		if (this.appStore.configuration?.data?.context) {
			const { data } = this.appStore.configuration;
			const updatedContextConfiguration = {
				...this.appStore.configuration,
				data: {
					...data,
					context: this.dataContextConfigurations.map((context) => {
						if (context.contextKey === targetContextKey) {
							return deepMergeObjectsWithOptions(
								deepMergeObjectOptions,
								context,
								dataContextOverride,
							);
						}
						return context;
					}),
				},
			};
			this.appStore.rawConfiguration = updatedContextConfiguration;
			this.updateInputs();
		}
	}

	@action async initialize() {
		const globalInputs = await this.inputStore.getAllFromStorage();
		this.updateInputs(globalInputs, true);
	}

	/**
	 * Updates the input overrides for the query contexts and loads the contexts with the new inputs.
	 *
	 * @param {Record<string, any>} [inputOverrides] - Optional input overrides for the query contexts.
	 * @param {boolean} [init=false] - Whether this is the initial update of the inputs.
	 * @returns {Promise<void>} - A Promise that resolves when all query contexts have been loaded with the new inputs.
	 */
	@action async updateInputs(inputOverrides?: any, init = false) {
		const { configuration } = this.appStore;
		const inputs = { ...this.inputs, ...inputOverrides };
		this.appStore.logDebug(`query.Query Store > ${init ? 'Init' : 'Update'} Inputs`, {
			current: this.inputs,
			next: inputs,
		});
		if (configuration && configuration.data && configuration.data.context) {
			this.appStore.dataContextStatus = init ? 'PENDING' : 'UPDATING';
			await this.loadContexts(this.dataContextConfigurations, inputs);
		}
		this.updatedInputs = inputs;
		this.appStore.contextsDidUpdate();
		this.appStore.dataContextStatus = 'READY';
	}

	/**
	 * This updates the QueryStore.updatedInputs, without updating context,
	 * but preps the input for the next loadContexts update.
	 *
	 * @param [inputOverrides]
	 */
	@action prepareInputsForContext(inputOverrides?: any) {
		this.updatedInputs = { ...this.inputs, ...inputOverrides };
	}

	/**
	 * Loads the specified query contexts and fetches their data from the Kurtosys API.
	 *
	 * @param {IQueryContextConfiguration[]} contexts - The query contexts to load.
	 * @param {Record<string, any>} [inputs] - Optional input overrides for the query contexts.
	 * @returns {Promise<void>} - A Promise that resolves when all query contexts have been loaded.
	 */
	@action
	async loadContexts(contexts: IQueryContextConfiguration[], inputs?: Record<string, any>) {
		const { configuration, dataContextSeed } = this.appStore;
		const batchPromises: IBatchPromiseElement[] = [];
		const queryContextCulture: IQueryContextCulture = {
			sourceCulture: (configuration && configuration.culture && configuration.culture.base) || 'en-GB',
			targetCulture:
				(inputs && inputs.culture) ||
				(configuration && configuration.culture && configuration.culture.default) ||
				'en-GB',
		};
		for (const context of contexts) {
			const queryContext = new QueryContext(context, dataContextSeed);
			batchPromises.push({
				identifier: {
					contextParentKey:
						(configuration && configuration.data && configuration.data.key) || this.defaultContextParentKey,
					contextKey: context.contextKey,
				},
				promiseFunc: () => queryContext.fetch(this.kurtosysApiStore.client, inputs || {}, queryContextCulture),
				responseFunc: (response: any, identifier: any) => {
					this.appStore.logDebug('query.Query Store > new query context', response);
					query.queryUtils.queryHelper.contextStore().set({
						...identifier,
						value: {
							context: response,
							contextConfiguration: context,
						},
					});
				},
			});
		}
		const batchOptions: IBatchOptions = {
			maxBatchSize: 10,
		};
		await common.commonUtils.batchExecutePromises(batchPromises, batchOptions);
	}

	/**
	 * Executes a query.
	 *
	 * @param {IQueryOptions} [options] - The query options.
	 * @param {Partial<IExecutionOptions>} [overrideExecutionOptions] - The execution options.
	 * @returns {any} - The query result.
	 */
	query(options?: IQueryOptions, overrideExecutionOptions?: Partial<IExecutionOptions>) {
		if (options) {
			const queryInstance = this.createQuery(options);
			const workingExecutionOptions = query.Query.mergeExecutionOptions(
				this.executionOptions,
				overrideExecutionOptions || {},
			);
			const queryResponse = queryInstance.execute(workingExecutionOptions);
			return queryResponse;
		}
	}

	@action
	setFundManager(fundManager: IFundManager) {
		this.fundManagerContext = fundManager;
	}

	createQuery = (options: IQueryOptions = { queryOptionsType: 'none' }) => {
		const translationHelper = this.translationStore && this.translationStore.translationHelper;
		return new query.Query(options, translationHelper);
	};

	/**
	 * Returns the request body for an entity with the given id.
	 *
	 * @param {string} id - The id of the entity to get the request body for.
	 * @returns {object} - The request body for the entity.
	 */
	getEntitiesRequestBodyById(id: string) {
		const current = this.getContext();
		if (current?.contextConfiguration?.entities) {
			const searchEntity = current.contextConfiguration.entities.find((searchEntity) => searchEntity.id === id);
			if (searchEntity) {
				const { requestBody, requestBodyQueries } = searchEntity;
				let workingRequestBody = requestBody || {};
				if (requestBodyQueries) {
					const queryResults = query.queryUtils.queryHelper.convertQueryMime(
						{ queryClass: query.Query },
						requestBodyQueries,
						this.executionOptions,
					);
					workingRequestBody = common.commonUtils.deepMergeObjects(requestBody || {}, queryResults);
				}

				return workingRequestBody;
			}
		}
	}

	/**
	 * Fetches entities for a given id and input overrides.
	 *
	 * @param {string} id - The id of the entity to fetch.
	 * @param {object} inputs - Optional input overrides for the entity.
	 * @param {string} [contextParentKey=this.defaultContextParentKey] - The parent key of the context.
	 * @param {string} [contextKey='default'] - The key of the context.
	 * @param {IEntitiesRequestOptions} [options] - Optional entities request options.
	 * @param {(contextConfiguration: IQueryContextConfiguration) => void} [contextConfigurationOverride] - Optional function to override the context configuration.
	 * @returns {Promise<any>} - A Promise that resolves with the fetched entities.
	 */
	async entitiesRequest(
		id: string,
		inputs: object,
		contextParentKey: string = this.defaultContextParentKey,
		contextKey = 'default',
		options?: IEntitiesRequestOptions,
		contextConfigurationOverride?: (contextConfiguration: IQueryContextConfiguration) => void,
	) {
		const contextAndConfiguration = this.getContext({ contextParentKey, contextKey });
		const { contextConfiguration } = contextAndConfiguration;
		if (contextConfiguration) {
			const { dataContextSeed } = this.appStore;
			const queryContext = new QueryContext(contextConfiguration, dataContextSeed);
			if (queryContext && contextConfiguration) {
				let fetchEntitiesOptions;
				const executionOptions = this.executionOptions;
				if (options) {
					fetchEntitiesOptions = options.fetchEntitiesOptions;
					if (options.overrideDynamicInputs === true) {
						if (!executionOptions.inputs) {
							executionOptions.inputs = {};
						}
						Object.assign(executionOptions.inputs, inputs);
					}
				}
				let workingContextConfiguration = contextConfiguration;
				if (contextConfigurationOverride) {
					workingContextConfiguration = JSON.parse(JSON.stringify(contextConfiguration));
					contextConfigurationOverride({ ...workingContextConfiguration });
				}
				return await queryContext.fetchEntities(
					this.client,
					id,
					inputs,
					workingContextConfiguration,
					executionOptions,
					fetchEntitiesOptions,
				);
			}
		}
	}

	/**
	 * Merges the results of queries and props for a given component orchestration.
	 *
	 * @template T - The type of the response.
	 * @param {IConfigurationComponentOrchestration} [componentOrchestration] - The component orchestration containing the queries and props.
	 * @param {any} [responseContext] - The response context.
	 * @returns {(T | undefined)} - The merged response or undefined if no component orchestration is provided.
	 */
	mergeQueriesAndProps<T>(
		componentOrchestration?: IConfigurationComponentOrchestration,
		responseContext?: any,
	): T | undefined {
		if (componentOrchestration) {
			const { props, queries } = componentOrchestration;
			const translationHelper = this.translationStore && this.translationStore.translationHelper;
			let workingExecutionOptions = this.executionOptions;
			if (responseContext) {
				workingExecutionOptions = {
					...workingExecutionOptions,
					responseContext,
				};
			}
			const queryResults = queries
				? query.queryUtils.queryHelper.convertQueryMime(
						{ queryClass: query.Query },
						queries,
						workingExecutionOptions,
						{
							translationHelper,
						},
				  )
				: {};
			const response: T = common.commonUtils.deepMergeObjectsWithOptions(
				{ arrayMergeStrategy: 'DeepMerge' },
				props || {},
				queryResults,
			);
			return response;
		}
	}

	public getPagingRequest(): IPagingOptions {
		if (!this.appStore.paginationOptions) {
			this.appStore.log('info', {
				message: 'paginationOptions not provided for getPagingRequest',
				additionalContext: 'QueryStore',
			});
			return;
		}

		const { query: options, overridingExecutionOptions } = this.appStore.paginationOptions;
		const { queryId, queryOptionsType, contextKey } = options;
		if (!queryId) {
			return;
		}

		const workingExecutionOptions = query.Query.mergeExecutionOptions(
			this.executionOptions,
			overridingExecutionOptions || {},
		);
		let contextConfiguration = workingExecutionOptions?.contextConfiguration;
		if (!contextConfiguration) {
			contextConfiguration = this.dataContextConfigurations.find((contextConfig) => {
				if (contextKey) {
					return contextConfig.contextKey === contextKey;
				}
				return contextConfig.contextKey === 'default';
			});
		}

		if (queryOptionsType === 'entities' && contextConfiguration?.entities) {
			const entitiesContext: IQueryContextEntity = contextConfiguration.entities.find(
				(entityConfig) => entityConfig.id === queryId,
			);
			if (entitiesContext) {
				let limit = entitiesContext.requestBody?.limit;
				if (entitiesContext.requestBodyQueries?.limit) {
					const limitQuery = new query.Query(entitiesContext.requestBodyQueries.limit);
					limit = limitQuery.execute(this.executionOptions);
				}
				let start = entitiesContext.requestBody?.start;
				if (entitiesContext.requestBodyQueries?.limit) {
					const startQuery = new query.Query(entitiesContext.requestBodyQueries.start);
					start = startQuery.execute(this.executionOptions);
				}
				this.appStore.log('info', {
					message: `${queryId} entities Limit & Start`,
					additionalContext: 'QueryStore',
					detail: {
						limit,
						start,
					},
				});
				// We can only paginate if there is a limit
				if (limit) {
					const request = {
						limit,
						start,
					};
					this.appStore.log('success', {
						message: 'getPagingRequest found',
						additionalContext: 'QueryStore',
						detail: {
							request,
							paginationOptions: this.appStore.paginationOptions,
						},
					});
					return request;
				}
			}
		}
		// TODO: queryOptionsType === 'datasets', etc.
		this.appStore.log('warning', {
			message: 'getPagingRequest not found',
			additionalContext: 'QueryStore',
			detail: {
				contextConfiguration,
				executionOptions: this.executionOptions,
				paginationOptions: this.appStore.paginationOptions,
			},
		});
		return;
	}

	public getPagingResult(): IPagingResult {
		if (!this.appStore.paginationOptions) {
			this.appStore.log('info', {
				message: 'paginationOptions not provided for getPagingResult',
				additionalContext: 'QueryStore',
			});
			return;
		}

		const { query: options, overridingExecutionOptions } = this.appStore.paginationOptions;
		const workingExecutionOptions = query.Query.mergeExecutionOptions(
			this.executionOptions,
			overridingExecutionOptions || {},
		);
		const contextAndConfiguration = query.queryUtils.queryHelper.getContextAndConfiguration(
			workingExecutionOptions,
			options,
		);
		if (contextAndConfiguration) {
			const { context, contextConfiguration } = contextAndConfiguration;
			const { queryId, queryOptionsType } = options;
			if (queryId && context?.entities && queryOptionsType === 'entities') {
				const entitiesResult = context.entities[queryId];
				const entitiesConfiguration = contextConfiguration?.entities.find((entity) => entity.id === queryId);
				let offset: number;
				if (entitiesConfiguration) {
					if (entitiesConfiguration.requestBody?.start) {
						offset = entitiesConfiguration.requestBody.start;
					} else if (entitiesConfiguration.requestBodyQueries?.start) {
						offset = this.query(
							entitiesConfiguration.requestBodyQueries.start,
							overridingExecutionOptions || {},
						);
					}
				}

				if (entitiesResult) {
					const result = {
						total: entitiesResult.total,
						limit: entitiesResult.limit,
						offset,
					};
					this.appStore.log('success', {
						message: 'getPagingResult found',
						additionalContext: 'QueryStore',
						detail: {
							result,
							paginationOptions: this.appStore.paginationOptions,
						},
					});
					return result;
				}
			}
		}
		// TODO: queryOptionsType === 'datasets', etc.
		this.appStore.log('warning', {
			message: 'getPagingResult not found',
			additionalContext: 'QueryStore',
			detail: {
				paginationOptions: this.appStore.paginationOptions,
			},
		});
		return;
	}
}
